import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useQuery, useQueryClient} from 'react-query'
import axios from 'axios'
import clsx from 'clsx'
import toaster from '../../../../Utils/toaster'
import {CommonLoading} from '../../../../Utils/commonLoading'
import {CommonPaginationModel} from '../../../../Utils/commonPagination'
import './CustomerMfaSettings.css'

const API_URL = process.env.REACT_APP_API_URL

type CustomerReportingUser = {
  userId: string
  name: string
  email: string
  role: string
  customerName: string
  isActive: boolean
  reportingEnabled: boolean
}

type CustomerReportingSettings = {
  enabledForAll: boolean
  featureEnabled: boolean
  enabledCount: number
  accountUserCount: number
  totalUsers: number
  page: number
  pageSize: number
  totalPages: number
  users: CustomerReportingUser[]
}

type UserOverrideMap = Record<string, boolean>

const normalizeSettings = (raw: Record<string, unknown>): CustomerReportingSettings => ({
  enabledForAll: Boolean(raw.enabledForAll ?? raw.EnabledForAll ?? false),
  featureEnabled: Boolean(raw.featureEnabled ?? raw.FeatureEnabled ?? false),
  enabledCount: Number(raw.enabledCount ?? raw.EnabledCount ?? 0),
  accountUserCount: Number(raw.accountUserCount ?? raw.AccountUserCount ?? 0),
  totalUsers: Number(raw.totalUsers ?? raw.TotalUsers ?? 0),
  page: Number(raw.page ?? raw.Page ?? 1),
  pageSize: Number(raw.pageSize ?? raw.PageSize ?? 10),
  totalPages: Number(raw.totalPages ?? raw.TotalPages ?? 0),
  users: ((raw.users ?? raw.Users ?? []) as Record<string, unknown>[]).map((user) => ({
    userId: String(user.userId ?? user.UserId ?? ''),
    name: String(user.name ?? user.Name ?? ''),
    email: String(user.email ?? user.Email ?? ''),
    role: String(user.role ?? user.Role ?? ''),
    customerName: String(user.customerName ?? user.CustomerName ?? ''),
    isActive: Boolean(user.isActive ?? user.IsActive ?? false),
    reportingEnabled: Boolean(user.reportingEnabled ?? user.ReportingEnabled ?? false),
  })),
})

const fetchCustomerReporting = async (
  customerId: number,
  page: number,
  pageSize: number,
  search: string
) => {
  const {data} = await axios.get<Record<string, unknown>>(
    `${API_URL}/CustomerSettings/reporting/${customerId}`,
    {params: {page, pageSize, search: search || undefined}}
  )
  return normalizeSettings(data)
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .slice(0, 2)
    .join('')
}

function getEffectiveReporting(
  user: CustomerReportingUser,
  masterOn: boolean,
  masterDirty: boolean,
  overrides: UserOverrideMap
): boolean {
  if (overrides[user.userId] !== undefined) return overrides[user.userId]!
  if (masterDirty) return masterOn
  return user.reportingEnabled
}

export function CustomerReportingSettingsView({customerId}: {customerId: number}) {
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [masterOn, setMasterOn] = useState(false)
  const [serverMasterOn, setServerMasterOn] = useState(false)
  const [masterDirty, setMasterDirty] = useState(false)
  const [overrides, setOverrides] = useState<UserOverrideMap>({})
  const [savedSnapshot, setSavedSnapshot] = useState(false)
  const [saving, setSaving] = useState(false)
  const syncedSummaryKeyRef = useRef('')

  const queryKey = useMemo(
    () => ['customer-reporting', customerId, page, rowsPerPage, search],
    [customerId, page, rowsPerPage, search]
  )

  const {data, refetch, isFetching, isError} = useQuery(
    queryKey,
    () => fetchCustomerReporting(customerId, page, rowsPerPage, search),
    {
      enabled: customerId > 0,
      keepPreviousData: true,
      staleTime: 0,
      refetchOnWindowFocus: false,
    }
  )

  useEffect(() => {
    if (isError) toaster('error', 'Failed to load Reporting users')
  }, [isError])

  useEffect(() => {
    setPage(1)
    setSearch('')
    setSearchInput('')
    setMasterDirty(false)
    setOverrides({})
    setSavedSnapshot(false)
    syncedSummaryKeyRef.current = ''
  }, [customerId])

  // Real-time debounce: automatically searches 350ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = searchInput.trim()
      if (trimmed !== search) {
        setPage(1)
        setSearch(trimmed)
      }
    }, 350)
    return () => clearTimeout(timer)
  }, [searchInput, search])

  useEffect(() => {
    if (!data) return

    const summaryKey = `${customerId}:${String(data.enabledForAll)}:${data.enabledCount}:${data.accountUserCount}`
    if (syncedSummaryKeyRef.current === summaryKey) return

    syncedSummaryKeyRef.current = summaryKey
    setServerMasterOn(data.enabledForAll)
    if (!masterDirty) setMasterOn(data.enabledForAll)
  }, [customerId, data, masterDirty])

  const users = data?.users ?? []
  const totalUsers = data?.totalUsers ?? 0
  const accountUserCount = data?.accountUserCount ?? 0
  const totalPages = Math.max(1, data?.totalPages ?? 1)

  const pendingCount = (masterDirty ? 1 : 0) + Object.keys(overrides).length
  const hasPendingChanges = pendingCount > 0

  const enabledSummaryCount = useMemo(() => {
    if (masterDirty) {
      const exceptions = Object.values(overrides).filter((value) => value !== masterOn).length
      return masterOn ? Math.max(0, accountUserCount - exceptions) : exceptions
    }

    const baseCount = data?.enabledCount ?? 0
    if (!data?.users || Object.keys(overrides).length === 0) return baseCount

    let delta = 0
    for (const user of data.users) {
      if (overrides[user.userId] !== undefined) {
        if (overrides[user.userId] && !user.reportingEnabled) {
          delta += 1
        } else if (!overrides[user.userId] && user.reportingEnabled) {
          delta -= 1
        }
      }
    }
    return Math.max(0, Math.min(accountUserCount, baseCount + delta))
  }, [accountUserCount, data?.enabledCount, data?.users, masterDirty, masterOn, overrides])

  const displayedMasterOn = useMemo(() => {
    if (masterDirty) {
      if (!masterOn) return false
      const hasAnyOff = Object.values(overrides).some((v) => v === false)
      return !hasAnyOff
    }
    return accountUserCount > 0 && enabledSummaryCount === accountUserCount
  }, [accountUserCount, enabledSummaryCount, masterDirty, masterOn, overrides])

  const handlePageChange = useCallback((nextPage: number) => {
    setPage(nextPage)
  }, [])

  const handleRowsPerPageChange = useCallback((nextRowsPerPage: number) => {
    setRowsPerPage(nextRowsPerPage)
    setPage(1)
  }, [])

  const handleSearchSubmit = () => {
    const trimmed = searchInput.trim()
    if (trimmed !== search) {
      setPage(1)
      setSearch(trimmed)
    }
  }

  const handleMasterToggle = () => {
    const nextMaster = !displayedMasterOn
    setMasterOn(nextMaster)
    setMasterDirty(true)
    setOverrides({})
    setSavedSnapshot(false)
  }

  const handleUserToggle = (user: CustomerReportingUser) => {
    const current = getEffectiveReporting(user, masterOn, masterDirty, overrides)
    const next = !current
    const serverValue = masterDirty ? masterOn : user.reportingEnabled

    setOverrides((prev) => {
      const updated = {...prev}
      if (next === serverValue) delete updated[user.userId]
      else updated[user.userId] = next
      return updated
    })

    setSavedSnapshot(false)
  }

  const handleReset = () => {
    setMasterOn(serverMasterOn)
    setMasterDirty(false)
    setOverrides({})
    setSavedSnapshot(false)
  }

  const handleSave = async () => {
    if (!hasPendingChanges) return

    setSaving(true)
    try {
      const payload: Record<string, unknown> = {
        applyToAllUsers: false,
        enabledForAll: masterOn,
        userOverrides: [] as Array<{userId: string; reportingEnabled: boolean}>,
      }

      if (masterDirty) {
        payload.applyToAllUsers = true
        payload.enabledForAll = masterOn
      }

      payload.userOverrides = Object.entries(overrides).map(([userId, reportingEnabled]) => ({
        userId,
        reportingEnabled,
      }))

      await axios.put(`${API_URL}/CustomerSettings/reporting/${customerId}`, payload)
      toaster('success', 'Reporting settings saved')
      setSavedSnapshot(true)
      setMasterDirty(false)
      setOverrides({})
      syncedSummaryKeyRef.current = ''
      await queryClient.invalidateQueries(['customer-reporting', customerId])
      const refreshed = await refetch()
      if (refreshed.data) {
        setMasterOn(refreshed.data.enabledForAll)
        setServerMasterOn(refreshed.data.enabledForAll)
        syncedSummaryKeyRef.current = `${customerId}:${String(refreshed.data.enabledForAll)}:${refreshed.data.enabledCount}:${refreshed.data.accountUserCount}`
      }
    } catch (error) {
      const message =
        axios.isAxiosError(error) && error.response?.data?.message
          ? String(error.response.data.message)
          : 'Failed to save Reporting settings'
      toaster('error', message)
    } finally {
      setSaving(false)
    }
  }

  const overrideHelp = masterOn
    ? 'Switch a user off to exempt them from the account rule.'
    : 'Switch a user on to grant Reporting to that person only.'

  return (
    <div className='customer-mfa-settings'>
      <div className='customer-mfa-settings__header'>
        <div>
          <h4 className='customer-mfa-settings__title'>Reporting</h4>
          <div className='customer-mfa-settings__subtitle'>
            Set one rule for the whole account, then override it for individual users where needed.
            These rules apply to active client users only. Admin users always keep access to Reporting.
          </div>
        </div>
        {accountUserCount > 0 && (
          <div className='customer-mfa-settings__summary'>
            <span className='customer-mfa-settings__summary-label'>Enabled for</span>
            <span className='customer-mfa-settings__summary-value'>
              {enabledSummaryCount} of {accountUserCount} active users
            </span>
          </div>
        )}
      </div>

      {data && !data.featureEnabled && (
        <div className='customer-mfa-settings__master-note customer-mfa-settings__master-note--off mt-4'>
          <span className='customer-mfa-settings__master-note-title'>Reporting feature is off.</span>
          <span className='customer-mfa-settings__master-note-body'>
            Turn on the Reporting feature in Features management before client users can open it.
          </span>
        </div>
      )}

      <div className='customer-mfa-settings__master-card'>
        <div className='customer-mfa-settings__master-row'>
          <div>
            <div className='customer-mfa-settings__master-title'>Enable Reporting functionality for all</div>
            <div className='customer-mfa-settings__master-help'>
              Every active user under this account will get access to reporting
            </div>
          </div>
          <button
            type='button'
            className={clsx(
              'customer-mfa-settings__switch',
              displayedMasterOn && 'customer-mfa-settings__switch--on'
            )}
            aria-pressed={displayedMasterOn}
            aria-label='Enable Reporting functionality for all'
            onClick={handleMasterToggle}
            disabled={saving}
          >
            <span className='customer-mfa-settings__switch-knob' />
          </button>
        </div>
      </div>

      <div className='customer-mfa-settings__overrides'>
        <div className='customer-mfa-settings__overrides-header'>
          <div>
            <div className='customer-mfa-settings__overrides-title'>Per-user overrides</div>
            <div className='customer-mfa-settings__overrides-help'>{overrideHelp}</div>
          </div>
          <div className='customer-mfa-settings__search-wrap'>
            <span className='customer-mfa-settings__search-icon' aria-hidden='true' />
            <input
              className='customer-mfa-settings__search'
              placeholder='Search name, email or role'
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSearchSubmit()
              }}
            />
          </div>
        </div>

        <div className='customer-mfa-settings__table-wrap'>
          <table className='customer-mfa-settings__table'>
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Customer</th>
                <th>Account</th>
                <th>Reporting</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && !isFetching && (
                <tr>
                  <td colSpan={5}>
                    <div className='customer-mfa-settings__empty'>No users match this search.</div>
                  </td>
                </tr>
              )}
              {users.map((user, index) => {
                const effective = getEffectiveReporting(user, masterOn, masterDirty, overrides)

                return (
                  <tr key={user.userId || `${user.email}-${index}`}>
                    <td>
                      <div className='customer-mfa-settings__user-cell'>
                        <div className='customer-mfa-settings__avatar'>{getInitials(user.name || user.email)}</div>
                        <div>
                          <div className='customer-mfa-settings__name'>{user.name || '—'}</div>
                          <div className='customer-mfa-settings__email'>{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className='customer-mfa-settings__cell-text'>{user.role || 'User'}</span>
                    </td>
                    <td>
                      <span className='customer-mfa-settings__cell-text'>{user.customerName}</span>
                    </td>
                    <td>
                      <span
                        className={clsx(
                          'customer-mfa-settings__status',
                          user.isActive
                            ? 'customer-mfa-settings__status--active'
                            : 'customer-mfa-settings__status--inactive'
                        )}
                      >
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className='customer-mfa-settings__mfa-cell'>
                        <button
                          type='button'
                          className={clsx(
                            'customer-mfa-settings__switch customer-mfa-settings__switch--sm',
                            effective && 'customer-mfa-settings__switch--on'
                          )}
                          aria-pressed={effective}
                          aria-label={`Reporting for ${user.name || user.email}`}
                          onClick={() => handleUserToggle(user)}
                          disabled={saving}
                        >
                          <span className='customer-mfa-settings__switch-knob' />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {totalUsers > 0 && (
          <div className='customer-mfa-settings__pagination'>
            <CommonPaginationModel
              currentPage={page}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              total={totalUsers}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </div>
        )}
      </div>

      {(hasPendingChanges || savedSnapshot) && (
        <div
          className={clsx(
            'customer-mfa-settings__pending',
            savedSnapshot && !hasPendingChanges && 'customer-mfa-settings__pending--saved'
          )}
        >
          <span className='customer-mfa-settings__pending-title'>
            {savedSnapshot && !hasPendingChanges
              ? 'Saved.'
              : `${pendingCount} ${pendingCount === 1 ? 'change' : 'changes'} not saved yet`}
          </span>
          <span className='customer-mfa-settings__pending-detail'>
            {savedSnapshot && !hasPendingChanges
              ? 'Reporting settings updated for this account.'
              : 'Nothing changes for these users until you press Save.'}
          </span>
        </div>
      )}

      <div className='customer-mfa-settings__footer'>
        <button
          type='button'
          className='customer-mfa-settings__btn-reset'
          onClick={handleReset}
          disabled={saving || !hasPendingChanges}
        >
          Reset
        </button>
        <button
          type='button'
          className='customer-mfa-settings__btn-save'
          onClick={handleSave}
          disabled={saving || !hasPendingChanges}
        >
          {hasPendingChanges ? 'Save changes' : 'Save'}
        </button>
      </div>

      {isFetching && <CommonLoading />}
    </div>
  )
}
